import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message, Image, Spin, Modal } from 'antd';
import { UserOutlined, LockOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { adminLogin, adminGaLogin, getCaptcha, clearError } from './authSlice';
import CryptoJS from 'crypto-js';
import '../../styles/login.css';

const GA_LENGTH = 6;
const CURSOR_SHAPES = ['circle', 'diamond', 'triangle', 'ring', 'cross'];
const CURSOR_TRAIL_LIMIT = 16;
const CURSOR_TRAIL_LIFETIME = 720;
const CURSOR_TRAIL_INTERVAL = 48;

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, captcha } = useSelector((state) => state.auth);
  
  const [form] = Form.useForm();
  const [adminCaptchaCode, setAdminCaptchaCode] = useState('');
  const [gaModalVisible, setGaModalVisible] = useState(false);
  const [loginTicket, setLoginTicket] = useState('');
  const [gaCodeArr, setGaCodeArr] = useState(Array(GA_LENGTH).fill(''));
  const [gaSubmitting, setGaSubmitting] = useState(false);
  const [cursorTrails, setCursorTrails] = useState([]);
  const gaInputRefs = useRef([]);
  const pageRef = useRef(null);
  const trailIdRef = useRef(0);
  const lastTrailTimeRef = useRef(0);

  // 密码加密函数
  const encryptPassword = (password) => {
    return CryptoJS.SHA256(password).toString();
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // 组件挂载时自动加载验证码
  useEffect(() => {
    dispatch(getCaptcha());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (captcha.error) {
      message.error(captcha.error);
    }
  }, [captcha.error]);

  // 弹框打开时清空并聚焦第一格
  useEffect(() => {
    if (gaModalVisible) {
      setGaCodeArr(Array(GA_LENGTH).fill(''));
      setTimeout(() => gaInputRefs.current[0]?.focus(), 100);
    }
  }, [gaModalVisible]);

  // 6位填满自动提交
  useEffect(() => {
    if (gaModalVisible && gaCodeArr.every((d) => /^\d$/.test(d)) && !isLoading && !gaSubmitting) {
      handleGaSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gaCodeArr.join('')]);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return undefined;

    const handleMove = (event) => {
      const now = Date.now();
      if (now - lastTrailTimeRef.current < CURSOR_TRAIL_INTERVAL) return;
      lastTrailTimeRef.current = now;

      const rect = page.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const id = trailIdRef.current;
      trailIdRef.current += 1;

      const trail = {
        id,
        x,
        y,
        shape: CURSOR_SHAPES[id % CURSOR_SHAPES.length],
        rotation: (id * 37) % 360,
      };

      setCursorTrails((prev) => [...prev, trail].slice(-CURSOR_TRAIL_LIMIT));

      window.setTimeout(() => {
        setCursorTrails((prev) => prev.filter((item) => item.id !== id));
      }, CURSOR_TRAIL_LIFETIME);
    };

    page.addEventListener('mousemove', handleMove);
    return () => page.removeEventListener('mousemove', handleMove);
  }, []);

  // 管理员登录
  const handleAdminLogin = async (values) => {
    try {
      const result = await dispatch(adminLogin({
        username: values.username,
        password: encryptPassword(values.password),
        captchaId: captcha.captchaId,
        captchaCode: values.captchaCode
      })).unwrap();

      if (result.needGa) {
        setLoginTicket(result.loginTicket);
        setGaModalVisible(true);
        return;
      }

      message.success('登录成功');
      navigate('/dashboard');
    } catch (error) {
      // 错误已在slice中处理
      // 登录失败后刷新验证码
      dispatch(getCaptcha());
      setAdminCaptchaCode('');
    }
  };

  // 单格输入
  const handleGaInputChange = (index, value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 1) {
      // 粘贴场景：分发到各格
      const next = Array(GA_LENGTH).fill('');
      digits.split('').forEach((ch, i) => {
        if (i < GA_LENGTH) next[i] = ch;
      });
      setGaCodeArr(next);
      gaInputRefs.current[Math.min(digits.length, GA_LENGTH) - 1]?.focus();
      return;
    }
    setGaCodeArr((prev) => {
      const next = [...prev];
      next[index] = digits;
      return next;
    });
    if (digits && index < GA_LENGTH - 1) {
      gaInputRefs.current[index + 1]?.focus();
    }
  };

  // 退格回退
  const handleGaKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !gaCodeArr[index] && index > 0) {
      e.preventDefault();
      setGaCodeArr((prev) => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
      gaInputRefs.current[index - 1]?.focus();
    }
  };

  // 粘贴整串
  const handleGaPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const digits = text.replace(/\D/g, '');
    if (digits) {
      handleGaInputChange(0, digits);
    }
  };

  // 提交二次验证
  const handleGaSubmit = async () => {
    const gaCode = gaCodeArr.join('');
    if (!/^\d{6}$/.test(gaCode)) {
      message.warning('请输入完整的6位动态验证码');
      return;
    }
    setGaSubmitting(true);
    try {
      await dispatch(adminGaLogin({
        loginTicket,
        gaCode
      })).unwrap();
      message.success('登录成功');
      setGaModalVisible(false);
      navigate('/dashboard');
    } catch (error) {
      // 错误已在slice中处理
      setGaCodeArr(Array(GA_LENGTH).fill(''));
      setTimeout(() => gaInputRefs.current[0]?.focus(), 100);
    } finally {
      setGaSubmitting(false);
    }
  };

  const handleCloseGaModal = () => {
    setGaModalVisible(false);
    setLoginTicket('');
    setGaCodeArr(Array(GA_LENGTH).fill(''));
    form.resetFields();
  };

  // 获取图形验证码
  const handleGetCaptcha = async () => {
    try {
      await dispatch(getCaptcha()).unwrap();
      message.success('验证码已刷新');
      setAdminCaptchaCode('');
    } catch (error) {
      message.error(error || '获取验证码失败');
    }
  };

  return (
    <div className="login-page login-page--admin" ref={pageRef}>
      <div className="login-page__decor">
        <div className="login-page__decor-circle login-page__decor-circle--1" />
        <div className="login-page__decor-circle login-page__decor-circle--2" />
        <div className="login-page__decor-geo login-page__decor-geo--tl" />
        <div className="login-page__decor-geo login-page__decor-geo--br" />
      </div>

      <div className="login-page__starry" aria-hidden="true">
        <div className="login-page__stars login-page__stars--far" />
        <div className="login-page__stars login-page__stars--mid" />
        <div className="login-page__stars login-page__stars--near" />
      </div>

      <div className="login-page__cursor-trail" aria-hidden="true">
        {cursorTrails.map((trail) => (
          <span
            key={trail.id}
            className="login-page__cursor-geo"
            style={{ left: trail.x, top: trail.y }}
          >
            <i
              className={`login-page__cursor-geo-shape login-page__cursor-geo-shape--${trail.shape}`}
              style={{ '--geo-rotate': `${trail.rotation}deg` }}
            />
          </span>
        ))}
      </div>

      <div className="login-card">
        <div className="login-card__brand login-card__brand--admin">
          <div className="login-card__kente-strip" />
          <div className="login-card__brand-badge">
            <SafetyCertificateOutlined />
            <span>Africa FinTech</span>
          </div>
          <h1 className="login-card__brand-title">金融后台管理系统</h1>
          <p className="login-card__brand-slogan">深耕非洲 · 安全 · 高效 · 可靠</p>
          <ul className="login-card__brand-features">
            <li>覆盖非洲多国支付网络</li>
            <li>实时交易数据监控</li>
            <li>全链路加密传输</li>
          </ul>
          <div className="login-card__illustration">
            <img src="/images/login-admin-illustration.svg" alt="" />
          </div>
        </div>

        <div className="login-card__form">
          <div className="login-card__form-header">
            <h2 className="login-card__form-title">管理员登录</h2>
            <p className="login-card__form-subtitle">请输入管理员账号和密码</p>
          </div>

          <Form
            form={form}
            name="adminLogin"
            onFinish={handleAdminLogin}
            autoComplete="off"
            size="large"
          >
            <Form.Item
              name="username"
              rules={[
                { required: true, message: '请输入管理员用户名' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="管理员用户名"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6位' }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="密码"
              />
            </Form.Item>

            <Form.Item className="login-card__captcha-group">
              <label className="login-card__captcha-label">图形验证码</label>
              <div className="login-card__captcha-row">
                <Form.Item
                  name="captchaCode"
                  rules={[
                    { required: true, message: '请输入验证码' },
                    { len: 4, message: '验证码为4位数字' }
                  ]}
                >
                  <Input
                    placeholder="请输入验证码"
                    value={adminCaptchaCode}
                    onChange={(e) => setAdminCaptchaCode(e.target.value.toUpperCase())}
                    maxLength={4}
                  />
                </Form.Item>
                {captcha.captchaImage && (
                  <div className="login-card__captcha-image" onClick={handleGetCaptcha} title="点击刷新验证码">
                    <Spin spinning={captcha.isLoading}>
                      <Image
                        src={captcha.captchaImage}
                        alt="图形验证码"
                        preview={false}
                      />
                    </Spin>
                  </div>
                )}
                <Button
                  className="login-card__captcha-refresh"
                  loading={captcha.isLoading}
                  onClick={handleGetCaptcha}
                  icon={<ReloadOutlined />}
                  title="刷新验证码"
                />
              </div>
              <p className="login-card__captcha-hint">点击图片或刷新按钮可更换验证码</p>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                className="login-card__submit login-card__submit--admin"
              >
                登录
              </Button>
            </Form.Item>
          </Form>

          <div className="login-card__security">
            <SafetyCertificateOutlined />
            <span>数据加密传输 · 保障系统安全</span>
          </div>
        </div>
      </div>

      <footer className="login-page__footer">
        © 2026 金融后台管理系统 · All rights reserved.
      </footer>

      <Modal
        className="login-ga-modal"
        title={null}
        open={gaModalVisible}
        onCancel={handleCloseGaModal}
        footer={null}
        width={420}
        closable={false}
        maskClosable={false}
      >
        <div className="login-ga-modal__body">
          <div className="login-ga-modal__icon login-ga-modal__icon--admin">
            <SafetyCertificateOutlined />
          </div>
          <h2 className="login-ga-modal__title">二次验证</h2>
          <p className="login-ga-modal__desc">
            该管理员已开启 Google 验证器，请输入 6 位动态验证码
          </p>

          <div className="login-ga-modal__digits" onPaste={handleGaPaste}>
            {gaCodeArr.map((digit, i) => (
              <Input
                key={i}
                ref={(el) => (gaInputRefs.current[i] = el)}
                className={`login-ga-modal__digit login-ga-modal__digit--admin${digit ? ' filled' : ''}`}
                value={digit}
                maxLength={6}
                inputMode="numeric"
                onChange={(e) => handleGaInputChange(i, e.target.value)}
                onKeyDown={(e) => handleGaKeyDown(i, e)}
              />
            ))}
          </div>

          <div className="login-ga-modal__actions">
            <Button
              size="large"
              className="login-ga-modal__btn"
              onClick={handleCloseGaModal}
            >
              取消
            </Button>
            <Button
              type="primary"
              size="large"
              className="login-ga-modal__btn login-ga-modal__btn--confirm--admin"
              loading={gaSubmitting || isLoading}
              onClick={handleGaSubmit}
            >
              确认
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Login;
